/**
 * Session Recovery Hook
 *
 * Detects when the browser tab regains focus after being idle and proactively
 * validates the user's session. This prevents the burst of MSAL iframe errors
 * and stale API calls that happen when a user returns to a tab after a long
 * period (e.g., overnight).
 *
 * Recovery strategy:
 * 1. Listen for `visibilitychange` events (tab becomes visible again)
 * 2. If the tab was hidden for longer than the idle threshold, probe MSAL
 *    with a silent token acquisition
 * 3. If the token is expired / interaction required, clear MSAL cache and
 *    redirect to login immediately — BEFORE any dashboard API calls fire
 * 4. Also detect stale deployment assets and force a page reload
 */

import { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { getMsalInstance, tokenRequest } from '../config/authConfig';
import { setTokenExpiredWithFailedRefresh, clearEntraAccount } from '../store/slices/authSlice';
import { isAuthRedirectInProgress, markAuthRedirectInProgress } from '../utils/authRedirect';
import { loginRequest } from '../config/authConfig';
import { logger } from '../utils/logger';
import type { RootState } from '../store';
import type { AppDispatch } from '../store';

/**
 * Minimum time (ms) the tab must be hidden before we run session recovery.
 * Set to 5 minutes — short enough to catch expired sessions before API calls,
 * long enough to avoid unnecessary checks on quick tab switches.
 */
const IDLE_THRESHOLD_MS = 5 * 60 * 1000;

/**
 * Minimum time between recovery checks to prevent rapid-fire validation
 * if the user keeps switching tabs.
 */
const CHECK_COOLDOWN_MS = 30_000;

/**
 * Hook to recover expired sessions when the user returns to a hidden tab.
 *
 * Should be called once in App.tsx after authentication initialization.
 */
export function useSessionRecovery(): void {
  const dispatch = useDispatch<AppDispatch>();
  const { isAuthenticated, authReadyState } = useSelector((state: RootState) => state.auth);

  const hiddenAtRef = useRef<number | null>(null);
  const lastCheckRef = useRef<number>(0);
  const checkingRef = useRef(false);

  const validateSessionOnResume = useCallback(async () => {
    // Only validate if user was previously authenticated and auth is fully ready
    if (!isAuthenticated || authReadyState !== 'ready') {
      return;
    }

    // Prevent concurrent checks
    if (checkingRef.current) return;

    // Enforce cooldown
    const now = Date.now();
    if (now - lastCheckRef.current < CHECK_COOLDOWN_MS) return;

    checkingRef.current = true;
    lastCheckRef.current = now;

    try {
      const msalInstance = getMsalInstance();
      const accounts = msalInstance.getAllAccounts();

      if (accounts.length === 0) {
        // No accounts in cache — session was already cleared
        logger.warn('[useSessionRecovery] No MSAL accounts found, clearing auth state');
        dispatch(clearEntraAccount());
        dispatch(setTokenExpiredWithFailedRefresh(true));
        return;
      }

      const account = accounts[0];

      try {
        // Probe token silently — this is the same check validateSession does,
        // but we do it here proactively before any API calls fire.
        await msalInstance.acquireTokenSilent({
          ...tokenRequest,
          account,
          forceRefresh: false, // Use cached token if still valid
        });

        // Token is still valid — nothing to do
        logger.info('[useSessionRecovery] Session still valid after tab resume');
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          logger.warn(
            '[useSessionRecovery] Session expired while tab was hidden — redirecting to login'
          );

          // Don't redirect if one is already in progress
          if (isAuthRedirectInProgress()) {
            return;
          }

          // Clear stale accounts to prevent further iframe attempts
          for (const acc of msalInstance.getAllAccounts()) {
            msalInstance.clearCache({ account: acc });
          }
          // Null out activeAccount so MSAL doesn't auto-inject the OID-based
          // username ({oid}@{tenant}.onmicrosoft.com) as login_hint
          msalInstance.setActiveAccount(null);

          // Clear Redux auth state
          dispatch(clearEntraAccount());

          // Save current path for redirect after re-login
          sessionStorage.setItem('auth_redirect_uri', window.location.pathname);

          // Redirect to Entra login
          markAuthRedirectInProgress();
          await msalInstance.loginRedirect({
            ...loginRequest,
          });
        } else {
          // Non-interaction error (network glitch, etc.) — log but don't force logout
          logger.warn('[useSessionRecovery] Token probe failed with non-interaction error:', {
            error: error instanceof Error ? error.message : String(error),
          });
        }
      }
    } catch (outerError) {
      logger.error('[useSessionRecovery] Unexpected error during session recovery:', outerError);
    } finally {
      checkingRef.current = false;
    }
  }, [isAuthenticated, authReadyState, dispatch]);

  useEffect(() => {
    if (!isAuthenticated || authReadyState !== 'ready') {
      return;
    }

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab is being hidden — record when
        hiddenAtRef.current = Date.now();
      } else {
        // Tab became visible again
        const hiddenAt = hiddenAtRef.current;
        hiddenAtRef.current = null;

        if (hiddenAt && Date.now() - hiddenAt >= IDLE_THRESHOLD_MS) {
          logger.info(
            `[useSessionRecovery] Tab was hidden for ${Math.round((Date.now() - hiddenAt) / 1000 / 60)}m — validating session`
          );
          validateSessionOnResume();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, authReadyState, validateSessionOnResume]);
}
